import { ActivatedRoute } from "@angular/router";
import { Component, HostListener, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { getMatchReviews } from "../api.service";
import { getApp } from "../app";
import { Part } from "../../../../shared/models/part";
import { Manufacturer } from "../../../../shared/models/manufacturer";
import {
  buildMatchReviewComposedId,
  MatchReviews
} from "../../../../shared/models/matchReviews";

interface ReviewComparisonRow {
  key:
    | "manufacturer"
    | "mpn"
    | "description"
    | "yteop"
    | "lifecycle"
    | "rohs";
  icon: string;
  label: string;
  importedValue: string;
  selectedValue: string;
}

@Component({
    selector: "app-manufacturer-matching",
    templateUrl: "./manufacturer-matching.component.html",
    styleUrls: ["./manufacturer-matching.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ManufacturerMatchingComponent implements OnInit {
  partNumber: string | null = null;
  manufacturerId: string | null = null;
  manufacturer = new Manufacturer();
  part = {} as Part;
  matchReviews: MatchReviews = new MatchReviews();
  comparisonRows: ReviewComparisonRow[] = [];
  private shouldRefreshOpener = false;
  isSavingMatchReview = false;

  constructor(private route: ActivatedRoute) {}

  app = getApp((app) => {
    this.app = app;
  });

  async ngOnInit() {
    this.manufacturerId = this.route.snapshot.paramMap.get("manufacturer");
    this.partNumber = this.route.snapshot.paramMap.get("part");
    if (this.manufacturerId == null) {
      return;
    }
    this.app.manufacturerMatching.manufacturer =
      await this.app.manufacturerMatching.getCurrentManufacturer(
        this.manufacturerId
      );
    this.manufacturer = this.app.manufacturerMatching.manufacturer;
    this.app.externalDataFilter.matchesCopy =
      this.app.manufacturerMatching.manufacturer.matches != null
        ? this.app.manufacturerMatching.manufacturer.matches
        : [];
    this.matchReviews = await this.loadMatchReviews();

    if (
      this.app.manufacturer.getMatchingStatusOfManufacturer(
        this.manufacturer
      ) === "manual" ||
      this.app.manufacturer.getMatchingStatusOfManufacturer(
        this.manufacturer
      ) === "unmatched"
    ) {
      this.manufacturer.manufacturerPartNumber = "";
      this.manufacturer.name = "";
      this.manufacturer.manufacturerPartDescription = "";
      this.manufacturer.datasheet = "";
      this.manufacturer.obsolescenceStatus = "";
    }
    if (this.partNumber == null) {
      this.comparisonRows = this.buildComparisonRows();
      return;
    }
    this.part = await this.app.manufacturerMatching.getCurrentPart(
      this.partNumber
    );

    if (
      this.app.manufacturer.getMatchingStatusOfManufacturer(
        this.manufacturer
      ) === "matched" ||
      this.app.manufacturer.getMatchingStatusOfManufacturer(
        this.manufacturer
      ) === "manualAssignment"
    ) {
      this.app.manufacturerMatching.updateCurrentMatchIfChangesExist(
        this.manufacturer,
        this.app.externalDataFilter.matchesCopy
      );
    }

    this.comparisonRows = this.buildComparisonRows();
    this.app.header.getHeaderTitle();
  }

  get reviewTitle() {
    return this.part.partNumber || this.partNumber || "";
  }

  get matchingStatus() {
    return this.app.manufacturer.getMatchingStatusOfManufacturer(
      this.manufacturer
    );
  }

  get matchingStatusLabel() {
    switch (this.matchingStatus) {
      case "matched":
        return this.app.text.RM.matched;
      case "manualAssignment":
        return this.app.text.matchingManufacturer.edited;
      case "manual":
        return this.app.text.RM.manual;
      case "override":
        return this.app.text.RM.override;
      default:
        return this.app.text.RM.unmatched;
    }
  }

  getStatusClass() {
    return `match-status-${this.matchingStatus}`;
  }

  get hasDatasheet() {
    return !this.isBlank(this.manufacturer.datasheet);
  }

  getSelectedValueLabel(row: ReviewComparisonRow) {
    switch (row.key) {
      case "lifecycle":
        return this.getLifecycleText(this.manufacturer.obsolescenceStatus);
      case "rohs":
        return this.getRoHSText(this.manufacturer.euRoHS);
      default:
        return row.selectedValue || "—";
    }
  }

  getSelectedValueStyle(row: ReviewComparisonRow) {
    switch (row.key) {
      case "lifecycle":
        return this.manufacturer.obsolescenceStatus
          ? this.app.field.getColor(
              this.app.fieldId.manufacturer.obsolescenceStatus,
              this.manufacturer.obsolescenceStatus
            )
          : null;
      case "rohs":
        return this.manufacturer.euRoHS
          ? this.app.field.getColor(
              this.app.fieldId.manufacturer.euRoHS,
              this.manufacturer.euRoHS
            )
          : null;
      default:
        return null;
    }
  }

  getImportedValueStyle(row: ReviewComparisonRow) {
    switch (row.key) {
      case "lifecycle":
        return this.part.obsolescenceStatus &&
          String(this.part.obsolescenceStatus) !== "withoutData"
          ? this.app.field.getColor(
              this.app.fieldId.part.obsolescenceStatus,
              this.part.obsolescenceStatus
            )
          : null;
      case "rohs":
        return this.part.euRoHS
          ? this.app.field.getColor(
              this.app.fieldId.part.euRoHS,
              this.part.euRoHS
            )
          : null;
      default:
        return null;
    }
  }

  isImportedValueBadge(row: ReviewComparisonRow) {
    return row.key === "lifecycle" || row.key === "rohs";
  }

  isSelectedValueBadge(row: ReviewComparisonRow) {
    return row.key === "lifecycle" || row.key === "rohs";
  }

  onManufacturerStatusChanged(updatedManufacturer: Manufacturer) {
    const previousStatus = this.matchingStatus;
    this.manufacturer = updatedManufacturer;
    this.comparisonRows = this.buildComparisonRows();
    const nextStatus =
      this.app.manufacturer.getMatchingStatusOfManufacturer(updatedManufacturer);
    if (
      previousStatus !== nextStatus &&
      (nextStatus === "unmatched" || nextStatus === "manualAssignment")
    ) {
      this.shouldRefreshOpener = true;
    }
  }

  closeMatchingTab() {
    if (this.isSavingMatchReview) {
      return;
    }

    this.refreshOpenerIfNeeded();
    this.app.routing.closeTab();
  }

  onSavingStateChanged(isSaving: boolean) {
    this.isSavingMatchReview = isSaving;
  }

  @HostListener("window:beforeunload", ["$event"])
  refreshOpenerIfNeeded(event?: BeforeUnloadEvent) {
    if (this.isSavingMatchReview) {
      event?.preventDefault();
      if (event) {
        event.returnValue = "";
      }
      return;
    }

    if (!this.shouldRefreshOpener) {
      return;
    }

    try {
      window.opener?.location.reload();
      this.shouldRefreshOpener = false;
    } catch (error) {
      this.shouldRefreshOpener = false;
    }
  }

  private async loadMatchReviews() {
    const composedID = buildMatchReviewComposedId(
      this.manufacturer.partNumber,
      this.manufacturer.manufacturerPartNumberRaw,
      this.manufacturer.nameRaw
    );

    if (composedID !== "") {
      const composedMatchReviews = await getMatchReviews(
        this.app.customers.expectCurrent,
        composedID
      );
      if (this.hasMatchReviewDocument(composedMatchReviews)) {
        return composedMatchReviews;
      }
    }

    return new MatchReviews();
  }

  private hasMatchReviewDocument(matchReviews: MatchReviews | null | undefined) {
    return matchReviews?._id != null && matchReviews._id !== "";
  }

  private buildComparisonRows(): ReviewComparisonRow[] {
    const importedMpn = this.getFirstValue(
      this.manufacturer.manufacturerPartNumberRaw,
      this.manufacturer.manufacturerPartNumberClean,
      this.manufacturer.manufacturerPartNumber
    );
    const importedManufacturer = this.getFirstValue(
      this.manufacturer.nameRaw,
      this.manufacturer.nameClean,
      this.manufacturer.name
    );
    const importedDescription = this.getFirstValue(
      this.manufacturer.manufacturerPartDescriptionRaw,
      this.manufacturer.manufacturerPartDescriptionClean,
      this.manufacturer.manufacturerPartDescription
    );
    const importedYTEOP = this.getFirstValue(
      this.part["estimatedYearsToEOL"],
      this.part.forecast
    );

    const importedLifecycle = this.getLifecycleText(this.part.obsolescenceStatus);
    const importedRoHS = this.getRoHSText(this.part.euRoHS);

    return [
      {
        key: "manufacturer",
        icon: "fas fa-industry",
        label: this.getComparisonLabel(this.app.fieldId.manufacturer.nameRaw),
        importedValue: importedManufacturer || "—",
        selectedValue: this.manufacturer.name || "—"
      },
      {
        key: "mpn",
        icon: "fas fa-hashtag",
        label: this.getComparisonLabel(
          this.app.fieldId.manufacturer.manufacturerPartNumberRaw
        ),
        importedValue: importedMpn || "—",
        selectedValue: this.manufacturer.manufacturerPartNumber || "—"
      },
      {
        key: "description",
        icon: "far fa-file-alt",
        label: this.getComparisonLabel(
          this.app.fieldId.manufacturer.manufacturerPartDescriptionRaw
        ),
        importedValue: importedDescription || "—",
        selectedValue: this.manufacturer.manufacturerPartDescription || "—"
      },
      {
        key: "yteop",
        icon: "fas fa-chart-line",
        label: this.app.text.RM.yteop,
        importedValue: importedYTEOP || "—",
        selectedValue: this.manufacturer.estimatedYearsToEOL || "—"
      },
      {
        key: "lifecycle",
        icon: "fas fa-wave-square",
        label: this.getComparisonLabel(this.app.fieldId.part.obsolescenceStatus),
        importedValue: importedLifecycle || "—",
        selectedValue: this.getLifecycleText(this.manufacturer.obsolescenceStatus)
      },
      {
        key: "rohs",
        icon: "fas fa-leaf",
        label: this.getComparisonLabel(this.app.fieldId.part.euRoHS),
        importedValue: importedRoHS || "—",
        selectedValue: this.getRoHSText(this.manufacturer.euRoHS)
      }
    ];
  }

  private isBlank(value?: string | null) {
    return String(value || "").trim() === "";
  }

  private getFirstValue(...values: Array<string | undefined | null>) {
    return values.find((value) => !this.isBlank(value)) || "";
  }

  private getComparisonLabel(fieldId: string) {
    return this.app.field.getLabel(fieldId).replace(/\s*\(\s*import\s*\)/i, "");
  }

  private getLifecycleText(value?: string) {
    if (this.isBlank(value)) {
      return "";
    }

    if (value === "withoutData") {
      return "-";
    }

    const safeValue = value || "";
    return this.app.field.getOptionText(
      this.app.fieldId.manufacturer.obsolescenceStatus,
      safeValue
    );
  }

  private getRoHSText(value?: string) {
    if (this.isBlank(value)) {
      return "";
    }

    const safeValue = value || "";
    return this.app.field.getOptionText(
      this.app.fieldId.manufacturer.euRoHS,
      safeValue
    );
  }
}
